'use client';

import { c as _c } from "react/compiler-runtime";
import Image, { type ImageProps } from 'next/image';
import type { StaticImageData } from 'next/dist/shared/lib/get-img-props';
import { type ReactElement } from 'react';
import { type ThemeAssetSrc } from 'shared/assets/resolveThemeAsset';
import { useResolvedThemeAsset } from 'shared/assets/useResolvedThemeAsset';
type ThemedImageProps = Omit<ImageProps, 'src'> & {
  /**
   * Same as `next/image` `src` for local assets: pass the static import
   * (`import img from 'public/assets/images/...'`) or a string URL/path.
   * Dark mode selects the `.dark` sibling unless the asset is exempt.
   */
  src: ThemeAssetSrc;
};
const getStaticImageData = (src: ThemeAssetSrc): StaticImageData | null => {
  if (typeof src === 'string') {
    return null;
  }
  if ('default' in src) {
    return src.default;
  }
  if ('width' in src && 'height' in src && typeof src.width === 'number' && typeof src.height === 'number') {
    return src;
  }
  return null;
};

/**
 * Drop-in theme-aware replacement for `next/image` when `src` is a static
 * asset under `public/assets/images`. Keep existing imports and `src={img}`.
 *
 * When `src` is a static import, intrinsic `width` / `height` are preserved if
 * the caller did not set them (and `fill` is not used), including when dark mode
 * rewrites the URL to a `.dark` sibling string.
 */
const ThemedImage = t0 => {
  const $ = _c(23);
  let alt;
  let fill;
  let height;
  let imageProps;
  let src;
  let width;
  if ($[0] !== t0) {
    ({
      src,
      alt,
      width,
      height,
      fill,
      ...imageProps
    } = t0);
    $[0] = t0;
    $[1] = alt;
    $[2] = fill;
    $[3] = height;
    $[4] = imageProps;
    $[5] = src;
    $[6] = width;
  } else {
    alt = $[1];
    fill = $[2];
    height = $[3];
    imageProps = $[4];
    src = $[5];
    width = $[6];
  }
  const resolvedSrc = useResolvedThemeAsset(src);
  let t1;
  if ($[7] !== src) {
    t1 = getStaticImageData(src);
    $[7] = src;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const staticImage = t1;
  if (resolvedSrc === staticImage?.src && staticImage != null) {
    let t2;
    if ($[9] !== alt || $[10] !== fill || $[11] !== height || $[12] !== imageProps || $[13] !== staticImage || $[14] !== width) {
      t2 = <Image {...imageProps} src={staticImage} alt={alt} width={width} height={height} fill={fill} />;
      $[9] = alt;
      $[10] = fill;
      $[11] = height;
      $[12] = imageProps;
      $[13] = staticImage;
      $[14] = width;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  const resolvedWidth = width ?? (fill === true ? undefined : staticImage?.width);
  const resolvedHeight = height ?? (fill === true ? undefined : staticImage?.height);
  let t2;
  if ($[16] !== alt || $[17] !== fill || $[18] !== imageProps || $[19] !== resolvedHeight || $[20] !== resolvedSrc || $[21] !== resolvedWidth) {
    t2 = <Image {...imageProps} src={resolvedSrc} alt={alt} width={resolvedWidth} height={resolvedHeight} fill={fill} />;
    $[16] = alt;
    $[17] = fill;
    $[18] = imageProps;
    $[19] = resolvedHeight;
    $[20] = resolvedSrc;
    $[21] = resolvedWidth;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  return t2;
};
export default ThemedImage;
export type { ThemedImageProps };